import React, { useState, useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/lib/auth'
import { supabase } from '@/lib/supabase'
import { TIER_NAMES } from '@/lib/billing'
import type { PlanTier } from '@/lib/billing'
import { getInitials } from '@/lib/utils'
import AIButton from '@/components/AIButton'
import DunningBanner from '@/components/DunningBanner'
import NotificationBell from '@/components/NotificationBell'
import { CallManagerProvider } from '@/lib/callManager'
import { PresenceProvider } from '@/lib/presence'
import { CallOverlay } from '@/components/calls/CallOverlay'
import ThemeToggle from '@/components/ThemeToggle'
import {
  LayoutDashboard, TrendingUp, Truck, Star, Settings,
  ChevronRight, LogOut, Zap, Search, BarChart2, CreditCard,
  Megaphone, Brain, MessageSquare, Menu, X,
} from 'lucide-react'

// Category-grouped nav. Reuses the existing expandable-group mechanics
// (children[] + toggleExpand + isGroupActive + accordion) — no new
// machinery. Section-label markers (Operations/CRM/Modules/Platform) are
// gone — categories ARE the grouping. Two formerly top-level items now
// surface badges as CHILDREN under Communication: Requests (newRequests)
// and Team Chat (teamChatUnread). Their parent renders the sum when
// not expanded so a glance at a collapsed group still surfaces unread.
const NAV = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/app' },
  {
    icon: MessageSquare,
    label: 'Communication',
    children: [
      { label: 'Inbox', path: '/app/inbox' },
      { label: 'Requests', path: '/app/requests', badgeKey: 'newRequests' as const },
      { label: 'Team Chat', path: '/app/team-chat', badgeKey: 'teamChatUnread' as const },
    ],
  },
  {
    icon: Truck,
    label: 'Field Hub',
    children: [
      { label: 'Calendar', path: '/app/field' },
      { label: 'Live map', path: '/app/field?tab=map' },
      { label: 'Crew', path: '/app/field?tab=crew' },
      { label: 'Projects', path: '/app/projects' },
      { label: 'Vendors', path: '/app/vendors' },
      { label: 'Fleet', path: '/app/fleet' },
      { label: 'Incidents', path: '/app/incidents' },
    ],
  },
  {
    icon: TrendingUp,
    label: 'Sales',
    children: [
      { label: 'Contacts', path: '/app/contacts' },
      { label: 'Companies', path: '/app/companies' },
      { label: 'Deals', path: '/app/deals' },
      { label: 'Calendar', path: '/app/sales-calendar' },
      { label: 'My Tasks', path: '/app/tasks' },
      { label: 'Quotes', path: '/app/quotes' },
    ],
  },
  {
    icon: Megaphone,
    label: 'Marketing',
    children: [
      { label: 'Campaigns', path: '/app/marketing' },
      { label: 'Sequences', path: '/app/marketing?tab=sequences' },
      { label: 'Templates', path: '/app/marketing?tab=templates' },
      { label: 'Segments', path: '/app/marketing?tab=segments' },
    ],
  },
  {
    icon: Star,
    label: 'Reputation',
    children: [
      { label: 'Overview', path: '/app/reputation' },
      { label: 'Reviews', path: '/app/reputation?tab=reviews' },
      { label: 'Requests', path: '/app/reputation?tab=requests' },
      { label: 'NPS', path: '/app/reputation?tab=nps' },
    ],
  },
  {
    icon: CreditCard,
    label: 'Accounting',
    children: [
      { label: 'Invoices', path: '/app/invoices' },
      { label: 'Overdue', path: '/app/invoices?status=overdue' },
      { label: 'Recurring', path: '/app/invoices/recurring' },
      { label: 'Products', path: '/app/products' },
    ],
  },
  {
    icon: BarChart2,
    label: 'Reporting',
    children: [
      { label: 'Analytics', path: '/app/analytics' },
      { label: 'Forecast', path: '/app/reports' },
    ],
  },
  { icon: Brain, label: 'AI Hub', path: '/app/ai' },
  {
    icon: Settings,
    label: 'Settings',
    children: [
      { label: 'Settings', path: '/app/settings' },
      { label: 'Integrations', path: '/app/integrations' },
    ],
  },
]

export default function AppLayout() {
  const { profile, workspace, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [expanded, setExpanded] = useState<string[]>(['Communication'])
  const [collapsed, setCollapsed] = useState(false)

  // Mobile-overlay sidebar. Desktop (>= 768px) renders the sidebar inline as
  // today — behavior unchanged. Mobile (< 768px) hides it, shows a sticky
  // top bar with a hamburger, and slides the sidebar in as a fixed overlay
  // with a tap-to-close backdrop. matchMedia keeps `isMobile` reactive across
  // browser resizes; the second effect closes the overlay on any route change
  // (so a nav-item tap dismisses it) and on a resize back to desktop.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  )
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(max-width: 767px)')
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname, isMobile])

  // Team Chat unread count for the sidebar badge. Polls every 30s — realtime
  // is intentionally not used here: notifications don't need sub-second
  // freshness, and keeping AppLayout decoupled from chat-specific realtime
  // channels avoids cross-concern coupling.
  //
  // Sums unread across every conversation the current user is a participant
  // of — channels, DMs, and groups. Two-step query (joined conversations +
  // per-conversation last_read_at → parallel COUNT per conversation). N+1
  // with a cap of ~40 conversations per workspace (combined cap across all
  // three types) is acceptable for a 30s poll; flagged for future RPC if
  // the count grows or the badge becomes a hot path.
  const { data: unreadTeamChatCount = 0 } = useQuery({
    queryKey: ['team-chat-unread', workspace?.id, profile?.id],
    enabled: !!workspace?.id && !!profile?.id,
    refetchInterval: 30000,
    queryFn: async () => {
      if (!workspace?.id || !profile?.id) return 0
      const { data: rows, error } = await supabase
        .from('team_conversation_participants')
        .select('team_conversation_id, last_read_at, team_conversations!inner(workspace_id, type)')
        .eq('user_id', profile.id)
        .eq('team_conversations.workspace_id', workspace.id)
        .in('team_conversations.type', ['channel', 'dm', 'group'])
      if (error || !rows || rows.length === 0) return 0
      const counts = await Promise.all(rows.map(async (row: any) => {
        const since = row.last_read_at || new Date(0).toISOString()
        const { count } = await supabase
          .from('team_messages')
          .select('*', { count: 'exact', head: true })
          .eq('team_conversation_id', row.team_conversation_id)
          .gt('created_at', since)
          .neq('user_id', profile.id)
        return count || 0
      }))
      return counts.reduce((sum, n) => sum + n, 0)
    },
  })

  // New-request count for the Requests nav badge. Same 30s poll cadence
  // as Team Chat — sub-second freshness isn't required and realtime
  // here would mean tracking another channel. Workspace-scoped via RLS.
  const { data: newRequestsCount = 0 } = useQuery({
    queryKey: ['portal-requests-new-count', workspace?.id],
    enabled: !!workspace?.id,
    refetchInterval: 30000,
    queryFn: async () => {
      if (!workspace?.id) return 0
      const { count } = await supabase
        .from('portal_requests')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'new')
      return count || 0
    },
  })

  const toggleExpand = (label: string) => {
    setExpanded(prev =>
      prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]
    )
  }

  const isActive = (path: string) => {
    if (path === '/app') return location.pathname === '/app'
    return location.pathname.startsWith(path)
  }

  const isGroupActive = (children: { path: string }[]) =>
    children.some(c => location.pathname.startsWith(c.path.split('?')[0]))

  return (
    <CallManagerProvider>
    <PresenceProvider>
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--canvas)' }}>

      {/* Mobile-only backdrop. Dims content behind the sidebar overlay and
          dismisses on tap. Rendered only when isMobile && mobileOpen so it
          never affects desktop layout. */}
      {isMobile && mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
          style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'var(--overlay-scrim)' }}
        />
      )}

      {/* Sidebar — inline flex item on desktop (unchanged); fixed off-screen
          overlay on mobile that slides in via translateX when mobileOpen. */}
      <motion.aside
        initial={false}
        animate={isMobile ? { x: mobileOpen ? 0 : -300, width: 280 } : { x: 0, width: collapsed ? 60 : 220 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        style={{
          background: 'var(--sidebar-bg)',
          borderRight: '1px solid var(--sidebar-border)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          flexShrink: 0,
          ...(isMobile ? { position: 'fixed', top: 0, left: 0, height: '100vh', zIndex: 50 } : {}),
        }}
      >
        {/* Logo */}
        <div style={{ padding: '16px 14px 12px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid var(--sidebar-faint-bg)' }}>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: 'var(--logo-glow)' }}>
            <Zap style={{ width: 16, height: 16, color: 'var(--accent-ink)' }} />
          </div>
          <AnimatePresence>
            {!collapsed && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                <p style={{ fontSize: 14, fontWeight: 800, color: 'var(--sidebar-name-ink)', letterSpacing: '-0.02em', margin: 0 }}>
                  {workspace?.name || 'Orkestra'}
                </p>
                <p style={{ fontSize: 10, color: 'var(--sidebar-meta-ink)', margin: 0, letterSpacing: '0.04em' }}>
                  {TIER_NAMES[workspace?.plan_tier as PlanTier] || 'Starter'}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Notifications bell — top of sidebar (just under the logo, above
            search/nav) so it's prominent. Renders in both expanded and
            collapsed states (search hides in collapsed; bell does not).
            Same component instance as the mobile top-bar variant; they
            share the react-query cache via stable queryKeys. */}
        <div style={{ padding: '8px 8px 0' }}>
          <NotificationBell variant="sidebar" collapsed={collapsed} />
        </div>

        {/* Search shortcut */}
        <AnimatePresence>
          {!collapsed && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ padding: '10px 10px 6px' }}>
              <button style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: 'var(--sidebar-faint-bg)', border: '1px solid var(--sidebar-border)', borderRadius: 8, cursor: 'pointer', color: 'var(--sidebar-meta-ink)', fontSize: 12 }}>
                <Search style={{ width: 12, height: 12, flexShrink: 0 }} />
                <span style={{ flex: 1, textAlign: 'left' }}>Search...</span>
                <kbd style={{ fontSize: 9, padding: '1px 4px', background: 'var(--sidebar-kbd-bg)', borderRadius: 4, color: 'var(--sidebar-meta-ink)', fontFamily: 'monospace' }}>⌘K</kbd>
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Nav */}
        <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '4px 8px 8px', scrollbarWidth: 'none' }}>
          {NAV.map((item, i) => {
            const Icon = item.icon
            const hasChildren = !!(item.children && item.children.length > 0)
            const isExp = expanded.includes(item.label)
            const groupActive = hasChildren && isGroupActive(item.children!)
            const active = item.path ? isActive(item.path) : groupActive
            // Parent badge sum: total of all child badge counts. Only shown
            // on the parent row when the group is NOT expanded (children
            // are hidden) — so the user can see at a glance that there's
            // something unread/new inside. When expanded, the children
            // show their own pills and the parent is silent.
            const parentBadgeSum = hasChildren
              ? (item.children!.reduce((sum, c) => {
                  const k = (c as { badgeKey?: 'newRequests' | 'teamChatUnread' }).badgeKey
                  if (k === 'newRequests')    return sum + newRequestsCount
                  if (k === 'teamChatUnread') return sum + unreadTeamChatCount
                  return sum
                }, 0))
              : 0

            return (
              <div key={i}>
                <div
                  className={`sidebar-item ${active ? 'active' : ''}`}
                  onClick={() => {
                    if (hasChildren) {
                      if (!collapsed) toggleExpand(item.label)
                    } else if (item.path) {
                      navigate(item.path)
                    }
                  }}
                  style={{ justifyContent: collapsed ? 'center' : 'flex-start', padding: collapsed ? '8px' : '7px 10px' }}
                  title={collapsed ? (parentBadgeSum > 0 ? `${item.label} (${parentBadgeSum > 99 ? '99+' : parentBadgeSum})` : item.label) : undefined}
                >
                  {/* Icon (+ collapsed-mode unread dot for groups with badges). */}
                  <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon style={{ width: 15, height: 15 }} />
                    {collapsed && parentBadgeSum > 0 && (
                      <span
                        aria-hidden="true"
                        style={{
                          position: 'absolute', top: -2, right: -2,
                          width: 7, height: 7, borderRadius: '50%',
                          background: 'var(--accent)',
                          border: '1px solid var(--sidebar-bg)',
                        }}
                      />
                    )}
                  </div>
                  <AnimatePresence>
                    {!collapsed && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', overflow: 'hidden', gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
                        {hasChildren && !isExp && parentBadgeSum > 0 && (
                          <span style={{ minWidth: 18, padding: '1px 6px', borderRadius: 100, background: 'var(--accent)', color: 'var(--accent-ink)', fontSize: 10, fontWeight: 700, textAlign: 'center', lineHeight: 1.5, flexShrink: 0 }}>
                            {parentBadgeSum > 99 ? '99+' : parentBadgeSum}
                          </span>
                        )}
                        {hasChildren && (
                          <motion.div animate={{ rotate: isExp ? 90 : 0 }} transition={{ duration: 0.15 }}>
                            <ChevronRight style={{ width: 12, height: 12, opacity: 0.4 }} />
                          </motion.div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <AnimatePresence>
                  {hasChildren && isExp && !collapsed && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.15 }}
                      style={{ overflow: 'hidden', paddingLeft: 14 }}
                    >
                      {item.children!.map((child, j) => {
                        const childPath = child.path.split('?')[0]
                        const childParam = child.path.includes('?') ? child.path.split('?')[1] : null
                        const childActive = childParam
                          ? location.pathname === childPath && location.search === `?${childParam}`
                          : location.pathname === childPath && !location.search
                        const k = (child as { badgeKey?: 'newRequests' | 'teamChatUnread' }).badgeKey
                        const childBadge =
                          k === 'newRequests'    ? newRequestsCount
                          : k === 'teamChatUnread' ? unreadTeamChatCount
                          : 0
                        return (
                          <div
                            key={j}
                            onClick={() => navigate(child.path)}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', borderRadius: 7, fontSize: 12, fontWeight: childActive ? 600 : 400, color: childActive ? 'var(--sidebar-ink-active)' : 'var(--sidebar-meta-ink)', cursor: 'pointer', transition: 'all 150ms ease', marginBottom: 1, background: childActive ? 'var(--sidebar-child-bg-active)' : 'transparent' }}
                            onMouseEnter={e => { if (!childActive) e.currentTarget.style.color = 'var(--sidebar-ink-hover)' }}
                            onMouseLeave={e => { if (!childActive) e.currentTarget.style.color = 'var(--sidebar-meta-ink)' }}
                          >
                            <div style={{ width: 4, height: 4, borderRadius: '50%', background: childActive ? 'var(--sidebar-icon-active)' : 'var(--sidebar-section-label)', flexShrink: 0 }} />
                            <span style={{ flex: 1 }}>{child.label}</span>
                            {childBadge > 0 && (
                              <span style={{ minWidth: 18, padding: '1px 6px', borderRadius: 100, background: 'var(--accent)', color: 'var(--accent-ink)', fontSize: 10, fontWeight: 700, textAlign: 'center', lineHeight: 1.5, flexShrink: 0 }}>
                                {childBadge > 99 ? '99+' : childBadge}
                              </span>
                            )}
                          </div>
                        )
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )
          })}
        </nav>

        {/* User */}
        <div style={{ padding: '10px 8px', borderTop: '1px solid var(--sidebar-faint-bg)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 8px', borderRadius: 10, cursor: 'pointer', transition: 'background 150ms ease' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--sidebar-faint-bg)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--avatar-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--accent-ink)', flexShrink: 0 }}>
              {getInitials(profile?.full_name || 'U')}
            </div>
            <AnimatePresence>
              {!collapsed && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ flex: 1, overflow: 'hidden' }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--sidebar-user-ink)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profile?.full_name || 'User'}</p>
                  <p style={{ fontSize: 10, color: 'var(--sidebar-meta-ink)', margin: 0, textTransform: 'capitalize' }}>{profile?.role}</p>
                </motion.div>
              )}
            </AnimatePresence>
            {!collapsed && (
              <button
                onClick={async () => { await signOut(); navigate('/signin') }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--sidebar-meta-ink)', padding: 0, display: 'flex', alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--sidebar-danger)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--sidebar-meta-ink)'}>
                <LogOut style={{ width: 14, height: 14 }} />
              </button>
            )}
          </div>
        </div>
      </motion.aside>

      {/* Main content */}
      <main style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>
        {/* Mobile-only top bar with hamburger + workspace name. Sticky so it
            stays visible as the page scrolls. Hidden on desktop where the
            sidebar already provides workspace context. */}
        {isMobile && (
          <div style={{
            position: 'sticky', top: 0, zIndex: 30,
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 14px',
            background: 'var(--surface)',
            borderBottom: '1px solid var(--border-subtle)',
            flexShrink: 0,
          }}>
            <button
              type="button"
              onClick={() => setMobileOpen(o => !o)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-primary)', padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6 }}
            >
              {mobileOpen ? <X style={{ width: 20, height: 20 }} /> : <Menu style={{ width: 20, height: 20 }} />}
            </button>
            <p style={{ flex: 1, fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {workspace?.name || 'Orkestra'}
            </p>
            <ThemeToggle variant="topbar" />
            <NotificationBell variant="topbar" />
          </div>
        )}
        {/* Dunning banner — sits outside the route-keyed motion.div so it
            doesn't unmount/remount on navigation. Returns null for healthy
            billing states. */}
        <DunningBanner />
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15 }}
          style={{ flex: 1, minHeight: 0 }}
        >
          <Outlet />
        </motion.div>
      </main>

      <AIButton />
      <CallOverlay />
    </div>
    </PresenceProvider>
    </CallManagerProvider>
  )
}